import React from 'react'
import { Button } from '@/components/ui/button'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { InterestRateThresholdDto } from 'api/Interest-Rates/InterestRateDto'
import { CurrencyType } from 'api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface EditInterestRateThresholdFormValues {
  id: string
  currency: NameDto<CurrencyType>
  maxAmount: number
  minInterestRate: number
  maxInterestRate: number
}

const EditInterestRateThresholdFormUI: React.FC<
  FormikProps<EditInterestRateThresholdFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors } = props

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Currency</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='currency.id' component='select'>
                <option value='' disabled>
                  None
                </option>
                {(props.currencies || []).map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.id}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Max Amount</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='maxAmount' type='number' min='0' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.maxAmount && <FormBaseErrorMessage>{errors.maxAmount}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Min Interest Rate</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='minInterestRate' type='number' min='0' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.minInterestRate && (
            <FormBaseErrorMessage>{errors.minInterestRate}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Max Interest Rate</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='maxInterestRate' type='number' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.maxInterestRate && (
            <FormBaseErrorMessage>{errors.maxInterestRate}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} onClick={handleSubmitForm} key='button' type='button'>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  data: InterestRateThresholdDto
  permissions: PermissionDto[]
  currencies: WalletCurrencyDto[]

  onSubmit(values: FormSubmitValues<EditInterestRateThresholdFormValues>): Promise<void>
}

export const EditInterestRateThresholdForm = withFormik<
  OuterProps,
  EditInterestRateThresholdFormValues
>({
  mapPropsToValues: ({ data }) => {
    return {
      id: data.id,
      currency: data.currency,
      maxAmount: data.maxAmount,
      minInterestRate: data.minInterestRate,
      maxInterestRate: data.maxInterestRate,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<EditInterestRateThresholdFormValues> = {}
    if (!values.maxAmount) {
      errors.maxAmount = 'required'
    }
    if (!values.maxInterestRate) {
      errors.maxInterestRate = 'required'
    }
    if (values.minInterestRate === undefined) {
      errors.minInterestRate = 'required'
    }
    if (values.minInterestRate) {
      if (values.minInterestRate <= 0) {
        errors.minInterestRate = 'Min Interest rate should be greater than or equal 0'
      }
    }
    if (values.maxInterestRate) {
      if (values.maxInterestRate < 0) {
        errors.maxInterestRate = 'Max Interest Rate should be greater than 0'
      }
    }
    return errors
  },
  enableReinitialize: true,
})(EditInterestRateThresholdFormUI)

const dataToSubmit = (
  values: EditInterestRateThresholdFormValues,
  props: OuterProps
): FormSubmitValues<EditInterestRateThresholdFormValues> => {
  const { currencies } = props

  const currency = currencies.find((x) => x.id === values.currency.id)

  return { ...values, currency: currency || values.currency }
}
